import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Play, CheckCircle2, Clock } from "lucide-react";

export default function TestsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name:"", type:"Weekly", total_marks:100, duration_min:60, subject:"", date: new Date().toISOString().slice(0,10) });

  // Attempt flow
  const [attemptTest, setAttemptTest] = useState(null);
  const [attemptQuestions, setAttemptQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submittedResult, setSubmittedResult] = useState(null);

  // Leaderboard
  const [lbTestId, setLbTestId] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  const load = async () => setItems((await api.get("/tests")).data);
  useEffect(()=>{load()},[]);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/tests", { ...form, total_marks:Number(form.total_marks), duration_min:Number(form.duration_min) });
    setOpen(false); toast.success("Test scheduled"); load();
  };

  const startAttempt = async (test) => {
    try {
      const { data } = await api.get(`/tests/${test.id}/questions`);
      setAttemptTest(data.test);
      setAttemptQuestions(data.questions);
      setAnswers({});
      setSubmittedResult(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load test");
    }
  };

  const submitAttempt = async () => {
    try {
      const { data } = await api.post(`/tests/${attemptTest.id}/attempt`, { answers });
      setSubmittedResult(data);
      toast.success("Submitted! You scored " + data.percent + "%");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    }
  };

  const openLeaderboard = async (test) => {
    const { data } = await api.get(`/tests/${test.id}/leaderboard`);
    setLbTestId(test.id);
    setLeaderboard(data);
  };

  const canAttempt = user?.role === "student";
  const canCreate = ["super_admin","branch_admin","faculty"].includes(user?.role);

  return (
    <div>
      <PageHeader
        title="Tests & Exams"
        subtitle="Schedule, attempt, and analyze"
        actions={canCreate ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button className="btn-primary" data-testid="add-test-btn">+ New test</button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Schedule test</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <input required placeholder="Test name" data-testid="test-name-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/>
                <div className="grid grid-cols-2 gap-2">
                  <select data-testid="test-type-select" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md bg-white" value={form.type} onChange={e=>setForm({...form, type:e.target.value})}>
                    {["Weekly","Monthly","Grand","Mock","OMR"].map(x=><option key={x}>{x}</option>)}
                  </select>
                  <input placeholder="Subject" data-testid="test-subject-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.subject} onChange={e=>setForm({...form, subject:e.target.value})}/>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <input type="number" placeholder="Total marks" data-testid="test-marks-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.total_marks} onChange={e=>setForm({...form, total_marks:e.target.value})}/>
                  <input type="number" placeholder="Duration (min)" data-testid="test-duration-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.duration_min} onChange={e=>setForm({...form, duration_min:e.target.value})}/>
                  <input type="date" data-testid="test-date-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.date} onChange={e=>setForm({...form, date:e.target.value})}/>
                </div>
                <DialogFooter><button className="btn-primary" data-testid="test-save-btn">Save</button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        ) : null}
      />

      <DataTable
        testid="tests-table"
        columns={[
          { key:"name", label:"Test" },
          { key:"type", label:"Type" },
          { key:"subject", label:"Subject" },
          { key:"date", label:"Date" },
          { key:"total_marks", label:"Marks" },
          { key:"duration_min", label:"Duration", render: r => `${r.duration_min} min` },
          { key:"actions", label:"", render: r => (
            <div className="flex gap-2">
              {canAttempt && <button data-testid={`attempt-btn-${r.id}`} onClick={()=>startAttempt(r)} className="text-xs px-3 py-1 rounded-full bg-[#0A66C2]/10 text-[#0A66C2] hover:bg-[#0A66C2]/20 font-semibold inline-flex items-center gap-1"><Play size={12}/> Attempt</button>}
              <button data-testid={`leaderboard-btn-${r.id}`} onClick={()=>openLeaderboard(r)} className="text-xs px-3 py-1 rounded-full bg-[#EDEBE7] text-[#000000E6] hover:bg-[#E0DFDC] font-semibold inline-flex items-center gap-1"><Trophy size={12}/> Leaderboard</button>
            </div>
          )},
        ]}
        rows={items}
      />

      {/* Attempt Dialog */}
      <Dialog open={!!attemptTest} onOpenChange={(v)=>!v && setAttemptTest(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{submittedResult ? "Result" : "Attempt: " + attemptTest?.name}</DialogTitle></DialogHeader>
          {!submittedResult ? (
            <div className="space-y-4">
              <div className="text-xs text-[#00000099] flex items-center gap-1"><Clock size={12}/> Duration: {attemptTest?.duration_min} min · {attemptQuestions.length} questions</div>
              {attemptQuestions.map((q, i) => (
                <div key={q.id} className="p-3 rounded-md border border-[#E0DFDC]" data-testid={`q-${i}`}>
                  <div className="text-sm font-semibold text-[#000000E6] mb-2">Q{i+1}. {q.q}</div>
                  <div className="space-y-1.5">
                    {q.options.map((opt) => {
                      const chosen = answers[q.id] === opt;
                      return (
                        <label key={opt} className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${chosen ? 'border-[#0A66C2] bg-[#EAF3FB]' : 'border-transparent hover:bg-[#0000000A]'}`} data-testid={`q-${i}-opt-${opt.slice(0,10)}`}>
                          <input type="radio" name={q.id} checked={chosen} onChange={()=>setAnswers({...answers, [q.id]: opt})} className="accent-[#0A66C2]"/>
                          <span className="text-sm">{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
              <DialogFooter>
                <button className="btn-primary" data-testid="attempt-submit-btn" onClick={submitAttempt} disabled={Object.keys(answers).length === 0}>Submit test</button>
              </DialogFooter>
            </div>
          ) : (
            <div className="text-center py-6" data-testid="attempt-result">
              <div className="w-20 h-20 rounded-full bg-[#EAF3FB] text-[#0A66C2] flex items-center justify-center mx-auto mb-3"><CheckCircle2 size={40}/></div>
              <div className="text-3xl font-bold text-[#0A66C2]">{submittedResult.percent}%</div>
              <div className="text-sm text-[#00000099] mt-1">{submittedResult.score} / {submittedResult.total} marks · {submittedResult.correct_count} / {submittedResult.total_questions} correct</div>
              <button className="btn-primary mt-6" onClick={()=>setAttemptTest(null)}>Close</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Leaderboard Dialog */}
      <Dialog open={!!lbTestId} onOpenChange={(v)=>!v && setLbTestId(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Leaderboard</DialogTitle></DialogHeader>
          <div className="divide-y divide-[#E0DFDC]" data-testid="leaderboard-list">
            {leaderboard.length === 0 && <div className="text-center text-sm text-[#00000099] py-6">No attempts yet</div>}
            {leaderboard.map((r) => (
              <div key={r.id || r.student_id} className="flex items-center gap-3 py-2.5" data-testid={`lb-row-${r.rank}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${r.rank === 1 ? 'bg-[#F5C300]/20 text-[#915907]' : r.rank === 2 ? 'bg-[#C0C0C0]/30 text-[#666]' : r.rank === 3 ? 'bg-[#CD7F32]/20 text-[#915907]' : 'bg-[#EDEBE7] text-[#00000099]'}`}>{r.rank}</div>
                <div className="flex-1">
                  <div className="text-sm font-semibold">{r.student_name}</div>
                  <div className="text-xs text-[#00000099]">{r.correct_count || 0}/{r.total_questions || 0} correct</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-[#0A66C2]">{r.percent}%</div>
                  <div className="text-[11px] text-[#00000099]">{r.score}/{r.total}</div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
